import React, { FormEvent, ReactNode, useEffect, useState } from 'react';
import _ from 'underscore';
import { css } from '@emotion/react';
import queryString from 'query-string';
import { Body } from '@leafygreen-ui/typography';
import { Select, Option, Size } from '@leafygreen-ui/select';
import { palette } from '@leafygreen-ui/palette';
import Banner from '@leafygreen-ui/banner';

// styles
import { selectInputStyle } from 'js/common/components/Vercel/styles/common';

// utils
import { CloudTeams, sendError } from 'js/common/utils/bugsnag';
import * as clusterDescriptionUtils from 'js/project/nds/clusters/util/clusterDescriptionUtils';

// services
import { organization } from 'js/common/services/api';
import clusterDescriptionsApi from 'js/common/services/api/nds/clusterDescriptionsApi';
import networkSettingsApi from 'js/common/services/api/nds/networkSettingsApi';
import * as api from 'js/common/services/api';

// contexts
import { useRequestParams } from 'js/common/context/RequestParamsContext';

// types
import { Search } from 'js/auth/types/search';
import { VercelProject, LinkedCluster } from 'js/common/types/vercel';

// components
import LineDivider from 'js/common/components/LineDivider';
import VercelProjectsToClusterLinker from 'js/common/components/Vercel/VercelProjectsToClusterLinker';
import IPAccessListField from './IPAccessListField';
import VercelLayout from './VercelLayout';

const lineDividerStyle = css`
  margin: 24px 0;
`;

const createNewStyle = css`
  margin-bottom: 48px;
  color: ${palette.blue.base};
  font-size: 15px;
  font-weight: bold;
  line-height: 24px;
  display: block;
  text-decoration: none;
`;

const bodyStyle = css`
  font-size: 13px;
  color: ${palette.gray.dark2};
  position: absolute;
  bottom: 64px;
`;
export interface GroupWithUserRoles {
  groupId: string;
  groupName: string;
  userRoles: Array<string>;
}

export interface Cluster {
  name: string;
  groupId: string;
}

export interface IpAccessList {
  [key: string]: string | boolean;
}

interface VercelCreateAndManageLinkingProps {
  isConfigureFlow?: boolean;
  orgName?: string;
  orgId?: string;
  location?: Pick<Location, 'search'>;
  windowLocation?: Pick<Location, 'assign'>;
  onChangeCluster?: (string) => void;
  onChangeVercelProjects?: (projects: Array<VercelProject>) => void;
  onSubmit?: (e: FormEvent<HTMLFormElement>, groupId: string) => void;
  isSubmitDisabled?: boolean;
  linkedClusters?: Array<LinkedCluster>;
  linkingError?: ReactNode;
}

export default function VercelCreateAndManageLinking({
  isConfigureFlow = false,
  orgName = '',
  orgId = '',
  location: { search } = { search: '' },
  windowLocation = window.location,
  onChangeCluster = () => {},
  onChangeVercelProjects = () => {},
  onSubmit = () => {},
  isSubmitDisabled = false,
  linkedClusters = [],
  linkingError,
}: VercelCreateAndManageLinkingProps) {
  const [projects, setProjects] = useState<Array<GroupWithUserRoles>>([]);
  const [selectedProjectName, setSelectedProjectName] = useState('');
  const [clusters, setClusters] = useState<Array<Cluster>>([]);
  const [isCheckboxSelected, setIsCheckboxSelected] = useState(false);
  const [showIpAccessList, setShowIpAccessList] = useState(false);
  const [hasFreeTierCluster, setHasFreeTierCluster] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { centralUrl } = useRequestParams();
  const { username } = queryString.parse(search) as Search;

  const displayOrgName = (orgName as string).replace(/\+/g, ' ');

  useEffect(() => {
    // get org's groups data from orgId
    async function getProjectsWithUserRoles() {
      try {
        const projectsWithUserRoles = await organization.getVisibleGroupsWithUserRoles({ centralUrl, orgId });
        setProjects(projectsWithUserRoles);
      } catch (error) {
        sendError({ error, team: CloudTeams.CoreIam });
      }
    }

    if (orgId !== '') {
      getProjectsWithUserRoles();
    }
  }, [orgId]);

  useEffect(() => {
    async function getClustersAndIpWhitelists() {
      try {
        const selectedProject = projects.find(
          (project: GroupWithUserRoles) => project.groupName === selectedProjectName
        );

        const getClusterDescriptions: Promise<Array<Cluster>> = clusterDescriptionsApi.getClusterDescriptions(
          selectedProject?.groupId,
          centralUrl
        );
        const getIpAWhitelist: Promise<Array<IpAccessList>> = networkSettingsApi.getIpWhitelist(
          selectedProject?.groupId,
          centralUrl
        );
        const [clusters, ipAccessList] = await Promise.all([getClusterDescriptions, getIpAWhitelist]);
        setClusters(clusters);

        if (clusters.some((cluster) => cluster['@provider'] === 'FREE')) {
          setHasFreeTierCluster(true);
        }

        const ipAccessListValues = _.pluck(ipAccessList, 'value');
        setShowIpAccessList(!_.contains(ipAccessListValues, '0.0.0.0/0'));
      } catch (error) {
        sendError({ error, team: CloudTeams.CoreIam });
      }
    }

    if (selectedProjectName !== '') {
      getClustersAndIpWhitelists();
    }
  }, [selectedProjectName]);

  const isProjectNameUnique = (name: string) => {
    return projects.length > 0 && !projects.find((project) => project.groupName === name);
  };

  const generateUniqueProjectOrClusterName = (nameFor) => {
    let uniqueName;
    let suffix = 0;
    let condition: boolean = true;

    if (nameFor === 'Project' && !projects.length) {
      return `Project 0`;
    }

    if (nameFor === 'Cluster' && !clusters.length) {
      return 'Cluster0';
    }

    do {
      if (nameFor === 'Project') {
        uniqueName = `Project ${suffix++}`;
        condition = isProjectNameUnique(uniqueName);
      } else {
        uniqueName = `Cluster${suffix++}`;
        condition = clusterDescriptionUtils.isNameUnique(
          uniqueName,
          clusters.map((cluster) => cluster.name)
        );
      }
    } while (!condition);
    return uniqueName;
  };

  const onClickCreateNewProject = async () => {
    try {
      await api.group.validateConfigLimits({
        orgId,
        numOfTeamsToAdd: 0,
        usernames: [decodeURIComponent(username as string)],
        centralUrl,
      });

      const groupName = generateUniqueProjectOrClusterName('Project');

      await organization.validateGroupName({ orgId, groupName, centralUrl });

      const { id: groupId } = await api.group.createGroup({
        orgId,
        group: groupName,
        useCNRegionsOnly: false,
        regionUsageRestrictions: 'NONE',
        centralUrl,
      });

      await api.group.grantAccess({ groupId, users: [], teams: [], apiUsers: [], centralUrl });

      windowLocation.assign(`/account/vercel/cluster/create${search}&groupId=${groupId}`);
    } catch (error) {
      sendError({ error, team: CloudTeams.CoreIam });
      setHasError(true);
    }
  };

  const onClickCreateNewCluster = () => {
    const selectedProject = projects.find((project: GroupWithUserRoles) => project.groupName === selectedProjectName);

    const groupId = selectedProject?.groupId;
    const clusterName = generateUniqueProjectOrClusterName('Cluster');
    windowLocation.assign(`/account/vercel/cluster/create${search}&groupId=${groupId}&clusterName=${clusterName}`);
  };

  const onClickSubmit = (e) => {
    const selectedProject = projects.find((project: GroupWithUserRoles) => project.groupName === selectedProjectName);

    if (selectedProject) {
      onSubmit(e, selectedProject.groupId);
    }
  };

  const getClusterNamesAvailableForLinking = () => {
    const clusterNames = clusters.map((cluster) => cluster.name);

    if (linkedClusters.length === 0) {
      return clusterNames;
    }

    const selectedProject = projects.find((project: GroupWithUserRoles) => project.groupName === selectedProjectName);

    let clusterNamesAvailableForLinking: Array<string> = [];

    linkedClusters.forEach((linkedCluster) => {
      if (
        (linkedCluster.groupId === selectedProject?.groupId && !['Cluster0'].includes(linkedCluster.clusterName)) ||
        linkedCluster.groupId !== selectedProject?.groupId
      ) {
        clusterNamesAvailableForLinking.push(linkedCluster.clusterName);
      } else {
        clusterNamesAvailableForLinking.push(`${linkedCluster.clusterName} (Already Linked)`);
      }
    });

    return clusterNamesAvailableForLinking;
  };

  return (
    <VercelLayout
      title={isConfigureFlow ? 'Add Another Cluster Link With Vercel' : 'Integrate Atlas with Vercel'}
      isForm
      formProps={{
        onSubmit: onClickSubmit,
        disabled: (showIpAccessList && !isCheckboxSelected) || !selectedProjectName || isSubmitDisabled,
        buttonText: isConfigureFlow ? 'Save' : 'Connect and Return to Vercel',
      }}
    >
      {linkingError}
      {hasError && (
        <Banner variant="danger" css={css({ margin: '24px 0px' })}>
          An error occurred with creating a new Atlas Project. Please use one of the existing Atlas projects to
          continue.
        </Banner>
      )}
      <Select
        size={Size.Default}
        name="organization"
        description={
          isConfigureFlow ? 'Your current Atlas organization' : 'Select an Atlas organization to integrate Vercel with'
        }
        defaultValue={displayOrgName}
        placeholder={displayOrgName}
        label="Organization"
        css={{ ...selectInputStyle, marginTop: '32px', marginBottom: '10px' }}
        disabled
      >
        <Option key={orgId} value={orgId}>
          {displayOrgName}
        </Option>
      </Select>
      <Select
        size={Size.Default}
        name="project"
        description="Select an Atlas project to integrate Vercel with"
        placeholder="Select an Atlas project"
        onChange={(project) => setSelectedProjectName(project)}
        value={selectedProjectName}
        label="Project"
        css={selectInputStyle}
      >
        {projects.map((project) => {
          const isEnabled = project.userRoles.includes('GROUP_OWNER');
          const projectName = project.groupName;
          return (
            <Option key={projectName} value={projectName} disabled={!isEnabled}>
              {isEnabled ? projectName : `${projectName} (Insufficient Permissions)`}
            </Option>
          );
        })}
      </Select>
      {!selectedProjectName && !isConfigureFlow && (
        <>
          <LineDivider css={lineDividerStyle}>or</LineDivider>
          <a href="#" css={createNewStyle} onClick={onClickCreateNewProject}>
            Create new Atlas Project
          </a>
        </>
      )}
      {selectedProjectName && (
        <>
          <VercelProjectsToClusterLinker
            clusterNames={getClusterNamesAvailableForLinking()}
            orgId={orgId}
            centralUrl={centralUrl}
            onChangeCluster={onChangeCluster}
            onChangeVercelProjects={onChangeVercelProjects}
          />
          {!hasFreeTierCluster && ( // only 1 free tier cluster can exist in a group
            <>
              <LineDivider css={lineDividerStyle}>or</LineDivider>
              <a href="#" onClick={onClickCreateNewCluster} css={createNewStyle}>
                Create new Atlas Cluster
              </a>
            </>
          )}
        </>
      )}
      {selectedProjectName && showIpAccessList && (
        <IPAccessListField onSelectCheckbox={() => setIsCheckboxSelected(!isCheckboxSelected)} />
      )}
      <Body css={bodyStyle}>
        A confirmation email will be sent to you with more detailed information regarding your new Vercel integration.
      </Body>
    </VercelLayout>
  );
}
